import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Home, PlusCircle, User, LogOut, Rocket, ShieldCheck, LogIn, UserPlus, Menu, X, Heart, LayoutGrid, BookOpen, Tag, Compass, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Header() {
  const { user, logout, config } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const authed = user && user !== false;
  const boostOn = !!config?.boost_enabled;
  const [open, setOpen] = useState(false);

  // Close drawer whenever the route changes
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleLogout = () => { setOpen(false); logout(); };

  const linkCls = "text-sm font-medium text-gray-700 hover:text-[#7A1E2A] transition-colors";
  const mobileLinkCls = "flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium text-[#1B0F14] hover:bg-[#FBF6EC] hover:text-[#7A1E2A] transition-colors";

  // Pill nav (mobile) — horizontally scrollable strip below header
  const isActive = (path) => loc.pathname === path || (path !== "/" && loc.pathname.startsWith(path));
  const pillBase = "shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border";
  const pill = (active) => active
    ? `${pillBase} bg-[#7A1E2A] text-[#FBF6EC] border-[#7A1E2A]`
    : `${pillBase} bg-white text-[#1B0F14] border-[#EFE3D0] hover:border-[#7A1E2A]/40 hover:text-[#7A1E2A]`;

  return (
    <header className="glass-nav sticky top-0 z-40" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Left: Hamburger (mobile) + Logo */}
        <div className="flex items-center gap-2">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className="md:hidden -ml-1 p-2 rounded-xl text-[#7A1E2A] hover:bg-[#7A1E2A]/8 transition-colors"
          >
            <Menu size={22} />
          </button>
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#7A1E2A] flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(122,30,42,0.5)] ring-1 ring-[#C9A24A]/40">
              <Home size={17} className="text-[#C9A24A]" />
            </div>
            <span className="font-display text-xl md:text-2xl font-medium text-[#7A1E2A] tracking-tight">To-Let Rentals</span>
          </Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/properties" data-testid="nav-browse" className={linkCls}>Browse</Link>
          <Link to="/discover" data-testid="nav-reels" className={`${linkCls} inline-flex items-center gap-1.5`}>
            Discover <span className="text-[9px] font-bold tracking-widest uppercase bg-[#C9A24A] text-[#1B0F14] px-1.5 py-0.5 rounded-md">Scroll</span>
          </Link>
          <Link to="/pricing" data-testid="nav-pricing" className={linkCls}>Pricing</Link>
          <Link to="/blog" data-testid="nav-blog" className={linkCls}>Guides</Link>
          {authed && <Link to="/dashboard" data-testid="nav-dashboard" className={linkCls}>Dashboard</Link>}
          {authed && (
            <Link to="/refer" data-testid="nav-refer" className="text-sm font-semibold text-[#C9A24A] hover:text-[#B08A2F] inline-flex items-center gap-1">
              <Gift size={14} /> Refer & Earn
            </Link>
          )}
          {authed && boostOn && (
            <Link to="/boost" data-testid="nav-boost" className="text-sm font-semibold text-[#7A1E2A] hover:text-[#5C1420] inline-flex items-center gap-1">
              <Rocket size={14} /> Boost
            </Link>
          )}
          {authed && ["owner", "admin", "sales"].includes(user.role) && (
            <Link to="/staff" data-testid="nav-staff-portal" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
              <ShieldCheck size={14} /> Staff
            </Link>
          )}
          {authed && ["owner", "admin"].includes(user.role) && (
            <Link to="/admin" data-testid="nav-admin" className="text-sm font-semibold text-[#7A1E2A] hover:text-[#5C1420] inline-flex items-center gap-1">
              <ShieldCheck size={14} /> Admin
            </Link>
          )}
          {authed && <Link to="/favorites" data-testid="nav-favorites" className={linkCls}>Saved</Link>}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {/* Mobile-only bold "List Free" button — sits parallel to the logo
              on the top bar for maximum landlord visibility on phones. Gold
              gradient + font-bold to shout the offer. Desktop keeps its own
              copy inside the md:flex clusters below. */}
          {!authed && (
            <button
              data-testid="mobile-header-list-free"
              onClick={() => {
                try {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: "post_listing_clicked",
                    cta_source: "mobile_header_top",
                    page: window.location.pathname,
                  });
                } catch (_) { /* noop */ }
                nav("/list-property");
              }}
              className="md:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-gradient-to-r from-[#E1BC5F] to-[#C9A24A] text-[#1B0F14] font-bold text-sm border border-[#A8842E] shadow-[0_4px_12px_-4px_rgba(201,162,74,0.5)] active:scale-95 transition-transform"
            >
              <PlusCircle size={15} /> List Free
            </button>
          )}
          {authed && (
            <button
              data-testid="mobile-header-list-property"
              onClick={() => {
                try {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: "post_listing_clicked",
                    cta_source: "mobile_header_top",
                    page: window.location.pathname,
                  });
                } catch (_) { /* noop */ }
                nav("/list-property");
              }}
              className="md:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#7A1E2A] text-[#FBF6EC] font-bold text-sm border border-[#5C1420] shadow-[0_4px_12px_-4px_rgba(122,30,42,0.5)] active:scale-95 transition-transform"
            >
              <PlusCircle size={15} /> List
            </button>
          )}
          {authed ? (
            <>
              <Button
                data-testid="nav-list-property"
                onClick={() => {
                  try {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: "post_listing_clicked",
                      cta_source: "header_authed",
                      page: window.location.pathname,
                    });
                  } catch (_) { /* noop */ }
                  nav("/list-property");
                }}
                className="tlr-gold-btn rounded-full h-10 px-4 md:px-5"
              >
                <PlusCircle size={16} className="md:mr-2" />
                <span className="hidden sm:inline">List Property</span>
              </Button>
              <div className="hidden md:flex items-center gap-2 pl-3 border-l border-[#EFE3D0] ml-2">
                <span data-testid="user-name" className="text-sm font-medium text-[#1B0F14]">{user.name}</span>
                <button onClick={logout} data-testid="logout-btn" aria-label="Logout" className="p-2 rounded-full text-gray-600 hover:bg-[#7A1E2A]/8 hover:text-[#7A1E2A] transition-colors">
                  <LogOut size={16} />
                </button>
              </div>
            </>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button
                data-testid="nav-list-property-guest"
                onClick={() => {
                  try {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: "post_listing_clicked",
                      cta_source: "header_guest",
                      page: window.location.pathname,
                    });
                  } catch (_) { /* noop */ }
                  nav("/list-property");
                }}
                className="tlr-gold-btn rounded-full h-10 px-4"
              >
                <PlusCircle size={15} className="mr-1.5" /> List Free
              </Button>
              <Button
                variant="ghost"
                data-testid="nav-login"
                onClick={() => nav("/login")}
                className="rounded-full h-10 px-4 text-[#7A1E2A] hover:bg-[#7A1E2A]/8 font-medium border border-[#EFE3D0]"
              >
                <LogIn size={15} className="mr-1.5" /> Log in
              </Button>
              <Button
                data-testid="nav-signup"
                onClick={() => nav("/register")}
                className="tlr-gold-btn rounded-full h-10 px-5"
              >
                <UserPlus size={15} className="mr-1.5" /> Sign up
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile pill nav — horizontal scroll strip */}
      <div className="md:hidden border-t border-[#EFE3D0]/70 bg-white/70 backdrop-blur-sm">
        <div
          data-testid="mobile-pill-nav"
          className="flex items-center gap-2 overflow-x-auto no-scrollbar px-4 py-2.5"
          style={{ scrollbarWidth: "none" }}
        >
          <Link to="/properties" data-testid="pill-browse" className={pill(isActive("/properties"))}>
            <LayoutGrid size={13} /> Browse
          </Link>
          <Link to="/discover" data-testid="pill-reels" className={pill(isActive("/discover"))}>
            <Compass size={13} /> Discover
          </Link>
          <Link to="/pricing" data-testid="pill-pricing" className={pill(isActive("/pricing"))}>
            <Tag size={13} /> Pricing
          </Link>
          <Link to="/blog" data-testid="pill-blog" className={pill(isActive("/blog"))}>
            <BookOpen size={13} /> Guides
          </Link>
          {authed && (
            <Link to="/dashboard" data-testid="pill-dashboard" className={pill(isActive("/dashboard"))}>
              <User size={13} /> Dashboard
            </Link>
          )}
          {authed && (
            <Link
              to="/refer"
              data-testid="pill-refer"
              className={`${pillBase} bg-gradient-to-r from-[#E1BC5F] to-[#C9A24A] text-[#1B0F14] border-[#A8842E] hover:brightness-105`}
            >
              <Gift size={13} /> Refer & Earn
            </Link>
          )}
          {authed && (
            <Link to="/favorites" data-testid="pill-favorites" className={pill(isActive("/favorites"))}>
              <Heart size={13} /> Saved
            </Link>
          )}
          {authed && boostOn && (
            <Link to="/boost" data-testid="pill-boost" className={pill(isActive("/boost"))}>
              <Rocket size={13} /> Boost
            </Link>
          )}
          {authed && user.role === "admin" && (
            <Link to="/admin" data-testid="pill-admin" className={pill(isActive("/admin"))}>
              <ShieldCheck size={13} /> Admin
            </Link>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-menu-drawer">
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <aside style={{ position: "fixed", top: 0, bottom: 0, left: 0, height: "100vh" }} className="w-[84%] max-w-sm bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#7A1E2A] flex items-center justify-center ring-1 ring-[#C9A24A]/40">
                  <Home size={15} className="text-[#C9A24A]" />
                </div>
                <span className="font-display text-lg font-medium text-[#7A1E2A]">Menu</span>
              </div>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="p-2 rounded-full text-gray-600 hover:bg-[#FBF6EC] hover:text-[#7A1E2A] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {authed && (
              <div className="px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#7A1E2A] text-[#C9A24A] flex items-center justify-center font-semibold">
                  {(user.name || "?").slice(0,1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div data-testid="mobile-user-name" className="text-sm font-semibold text-[#1B0F14] truncate">{user.name}</div>
                  <div className="text-xs text-gray-500 truncate">{user.email}</div>
                </div>
              </div>
            )}

            <nav className="flex-1 overflow-y-auto px-3 pb-3 flex flex-col gap-1">
              {/* Guest-only prominent "List Free" CTA at the top of the drawer.
                  Same style vocabulary as the authed Refer-and-Earn pill so
                  landlord traffic on mobile has a visible entry point without
                  needing to sign up first. */}
              {!authed && (
                <button
                  data-testid="mobile-nav-list-property-guest"
                  onClick={() => {
                    try {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "post_listing_clicked",
                        cta_source: "mobile_drawer_guest",
                        page: window.location.pathname,
                      });
                    } catch (_) { /* noop */ }
                    setOpen(false);
                    nav("/list-property");
                  }}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold bg-gradient-to-r from-[#FBF6EC] to-[#F2DFA9] text-[#1B0F14] border border-[#C9A24A]/60 hover:brightness-105 transition-all mb-1"
                >
                  <PlusCircle size={18} className="text-[#7A1E2A]" />
                  <span>List your property</span>
                  <span className="ml-auto text-[10px] font-bold tracking-widest uppercase bg-[#7A1E2A] text-[#FBF6EC] px-2 py-0.5 rounded-md">Free</span>
                </button>
              )}
              {authed && (
                <button
                  data-testid="mobile-nav-list-property-authed"
                  onClick={() => {
                    try {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "post_listing_clicked",
                        cta_source: "mobile_drawer_authed",
                        page: window.location.pathname,
                      });
                    } catch (_) { /* noop */ }
                    setOpen(false);
                    nav("/list-property");
                  }}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold bg-[#7A1E2A] text-[#FBF6EC] hover:brightness-110 transition-all mb-1"
                >
                  <PlusCircle size={18} className="text-[#C9A24A]" />
                  <span>List Property</span>
                </button>
              )}
              <Link to="/properties" data-testid="mobile-nav-browse" className={mobileLinkCls}>
                <LayoutGrid size={18} className="text-[#C9A24A]" /> Browse
              </Link>
              <Link to="/discover" data-testid="mobile-nav-reels" className={mobileLinkCls}>
                <Compass size={18} className="text-[#C9A24A]" /> Discover
                <span className="ml-auto text-[9px] font-bold tracking-widest uppercase bg-[#C9A24A] text-[#1B0F14] px-1.5 py-0.5 rounded-md">Scroll</span>
              </Link>
              <Link to="/pricing" data-testid="mobile-nav-pricing" className={mobileLinkCls}>
                <Tag size={18} className="text-[#C9A24A]" /> Pricing
              </Link>
              <Link to="/blog" data-testid="mobile-nav-blog" className={mobileLinkCls}>
                <BookOpen size={18} className="text-[#C9A24A]" /> Guides
              </Link>
              {authed && (
                <Link to="/dashboard" data-testid="mobile-nav-dashboard" className={mobileLinkCls}>
                  <User size={18} className="text-[#C9A24A]" /> Dashboard
                </Link>
              )}
              {authed && (
                <Link
                  to="/refer"
                  data-testid="mobile-nav-refer"
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-semibold bg-gradient-to-r from-[#FBF6EC] to-[#F2DFA9] text-[#1B0F14] border border-[#C9A24A]/60 hover:brightness-105 transition-all"
                >
                  <Gift size={18} className="text-[#7A1E2A]" />
                  <span>Refer & Earn</span>
                  <span className="ml-auto text-[10px] font-bold tracking-widest uppercase bg-[#7A1E2A] text-[#FBF6EC] px-2 py-0.5 rounded-md">+5 free</span>
                </Link>
              )}
              {authed && (
                <Link to="/favorites" data-testid="mobile-nav-favorites" className={mobileLinkCls}>
                  <Heart size={18} className="text-[#C9A24A]" /> Saved
                </Link>
              )}
              {authed && boostOn && (
                <Link to="/boost" data-testid="mobile-nav-boost" className={mobileLinkCls}>
                  <Rocket size={18} className="text-[#7A1E2A]" /> Boost
                </Link>
              )}
              {authed && ["owner", "admin", "sales"].includes(user.role) && (
                <Link to="/staff" data-testid="mobile-nav-staff" className={mobileLinkCls}>
                  <ShieldCheck size={18} className="text-emerald-600" /> Staff Portal (Leads)
                </Link>
              )}
              {authed && ["owner", "admin"].includes(user.role) && (
                <Link to="/admin" data-testid="mobile-nav-admin" className={mobileLinkCls}>
                  <ShieldCheck size={18} className="text-[#C9A24A]" /> Admin Console
                </Link>
              )}
            </nav>

            <div className="p-4">
              {authed ? (
                <button
                  data-testid="mobile-logout-btn"
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 h-12 rounded-full bg-[#7A1E2A] text-[#FBF6EC] font-semibold text-base shadow-[0_10px_25px_-8px_rgba(122,30,42,0.55)] ring-1 ring-[#C9A24A]/40 hover:bg-[#5C1420] active:scale-[0.98] transition-all"
                >
                  <LogOut size={18} /> Log out
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="ghost"
                    data-testid="mobile-nav-login"
                    onClick={() => { setOpen(false); nav("/login"); }}
                    className="rounded-full h-11 text-[#7A1E2A] font-medium border border-[#EFE3D0]"
                  >
                    <LogIn size={15} className="mr-1.5" /> Log in
                  </Button>
                  <Button
                    data-testid="mobile-nav-signup"
                    onClick={() => { setOpen(false); nav("/register"); }}
                    className="tlr-gold-btn rounded-full h-11"
                  >
                    <UserPlus size={15} className="mr-1.5" /> Sign up
                  </Button>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}
